import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, 
  Upload, 
  CheckCircle2, 
  Circle, 
  FileText, 
  Download, 
  Trash2,
  Plus,
  File,
  Image,
  FileSpreadsheet,
  Folder
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const getFileIcon = (fileType) => {
  if (fileType?.includes("image")) return <Image className="w-4 h-4 text-blue-400" />;
  if (fileType?.includes("spreadsheet") || fileType?.includes("excel")) return <FileSpreadsheet className="w-4 h-4 text-green-400" />;
  if (fileType?.includes("pdf")) return <FileText className="w-4 h-4 text-red-400" />;
  return <File className="w-4 h-4 text-neutral-400" />;
};

const MonthDetailPage = () => {
  const { year, month } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchMonthData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/status/${year}`, {
        headers: getAuthHeader()
      });
      const monthData = response.data.months.find(m => m.month === parseInt(month));
      setCategories(monthData?.categories || []);
      
      // Auto-expand categories with documents
      const expanded = {};
      monthData?.categories?.forEach(cat => {
        if (cat.document_count > 0) {
          expanded[cat.category] = true;
        }
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [year, month, getAuthHeader]);

  useEffect(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  const handleFileUpload = async (category, files) => {
    if (!files || files.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg", "image/jpg", "image/png",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    setUploading(category);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Formato non supportato: ${file.name}`);
        errorCount++;
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);
      formData.append("year", year);
      formData.append("month", month);

      try {
        await axios.post(`${API}/documents/upload`, formData, {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data"
          }
        });
        successCount++;
      } catch (error) {
        toast.error(`Errore: ${file.name}`);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file caricati con successo`);
      setExpandedCategories(prev => ({ ...prev, [category]: true }));
    }
    
    setUploading(null);
    fetchMonthData();
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: getAuthHeader()
      });
      toast.success("Documento eliminato");
      fetchMonthData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await axios.get(`${API}/documents/download/${docId}`, {
        headers: getAuthHeader()
      });
      
      const { file_data, file_type } = response.data;
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Errore nel download");
    }
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    setDragOver(category);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = (e, category) => {
    e.preventDefault();
    setDragOver(null);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(category, files);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const monthName = MONTHS[parseInt(month) - 1];
  const uploadedCount = categories.filter(c => c.uploaded).length;
  const totalCount = categories.length;
  const totalDocs = categories.reduce((sum, c) => sum + (c.document_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#A1A1AA] hover:text-[#D4AF37] -ml-2 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-3xl font-serif text-[#EDEDED]">{monthName}</h1>
              <p className="text-[#52525B] font-mono">{year}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-sm">
                <span className={`font-mono ${uploadedCount === totalCount ? 'text-[#10B981]' : 'text-[#D4AF37]'}`}>
                  {uploadedCount}/{totalCount}
                </span>
                <span className="text-[#52525B]">categorie</span>
              </div>
              <p className="text-xs text-[#52525B] mt-1">
                {totalDocs} documenti totali
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((cat) => (
              <div
                key={cat.category}
                className={`
                  bg-[#0A0A0A] border transition-all duration-300 rounded-lg overflow-hidden
                  ${cat.uploaded ? 'border-[#10B981]/30' : 'border-neutral-800'}
                  ${dragOver === cat.category ? 'border-[#D4AF37] bg-[#D4AF37]/5' : ''}
                `}
                onDragOver={(e) => handleDragOver(e, cat.category)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, cat.category)}
                data-testid={`category-${cat.category}`}
              >
                {/* Category Header */}
                <div 
                  className="p-6 flex items-center justify-between cursor-pointer hover:bg-neutral-900/50 transition-colors"
                  onClick={() => toggleCategory(cat.category)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${cat.uploaded ? 'bg-[#10B981]/10' : 'bg-neutral-800'}
                    `}>
                      {cat.uploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                      ) : (
                        <Circle className="w-5 h-5 text-neutral-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-[#EDEDED] font-medium">{cat.category_label}</h3>
                      <p className="text-xs text-[#52525B] mt-0.5">
                        {cat.document_count} {cat.document_count === 1 ? 'documento' : 'documenti'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Upload folder button */}
                    <label 
                      className="cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="file"
                        className="hidden"
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={(e) => handleFileUpload(cat.category, Array.from(e.target.files))}
                        disabled={uploading === cat.category}
                        data-testid={`upload-folder-${cat.category}`}
                      />
                      <span className={`
                        inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider font-medium transition-all border
                        ${uploading === cat.category 
                          ? 'border-neutral-700 text-neutral-500' 
                          : 'border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black cursor-pointer'}
                      `}>
                        <Folder className="w-4 h-4" />
                        Cartella
                      </span>
                    </label>
                    {/* Upload files button */}
                    <label 
                      className="cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        onChange={(e) => handleFileUpload(cat.category, Array.from(e.target.files))}
                        accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                        disabled={uploading === cat.category}
                        data-testid={`upload-input-${cat.category}`}
                      />
                      <span className={`
                        inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider font-medium transition-all
                        ${uploading === cat.category 
                          ? 'bg-neutral-800 text-neutral-500' 
                          : 'bg-[#D4AF37] text-black hover:bg-[#B5952F] cursor-pointer'}
                      `}>
                        {uploading === cat.category ? (
                          <div className="w-4 h-4 border-2 border-neutral-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            File
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Documents List - Expandable */}
                {expandedCategories[cat.category] && cat.documents && cat.documents.length > 0 && (
                  <div className="border-t border-neutral-800 bg-neutral-900/30">
                    <div className="p-4 space-y-2">
                      {cat.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded border border-neutral-800 hover:border-neutral-700 transition-colors group"
                          data-testid={`document-${doc.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {getFileIcon(doc.file_type)}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-[#EDEDED] truncate">{doc.filename}</p>
                              <p className="text-xs text-[#52525B] font-mono">
                                {new Date(doc.uploaded_at).toLocaleDateString("it-IT")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc.id, doc.filename)}
                              className="text-[#A1A1AA] hover:text-[#D4AF37] h-8 w-8 p-0"
                              data-testid={`download-${doc.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.id)}
                              className="text-[#A1A1AA] hover:text-red-500 h-8 w-8 p-0"
                              data-testid={`delete-${doc.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Drag and drop hint */}
        <p className="mt-8 text-center text-[#52525B] text-xs">
          Trascina i file su una categoria per caricarli • Supporta upload multiplo
        </p>
      </main>
    </div>
  );
};

export default MonthDetailPage;
